import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixAnalyticsModelV3SemanticModelsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/analytics-ng-model-api',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/analytics/v3/model',
        destPath: '/v3/model-api',
      },
    ],
    _: [
      {
        srcPath: '/_api/analytics-ng-model-api',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/analytics-ng-model-api',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_analytics-model_analytics-model';

/**
 * Retrieves data from a semantic model, given the provided fields, filters, sorting, and paging.
 *
 *
 * Query Model Data runs with these defaults, which you can override:
 *
 * - `paging.limit` is `50`
 * - `paging.offset` is `0`
 *
 * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 */
export function queryModelData(payload: object): RequestOptionsFactory<any> {
  function __queryModelData({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'interval.start' }, { path: 'interval.end' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.analytics.model.v3.model',
      method: 'POST' as any,
      methodFqn: 'wix.analytics.model.v3.SemanticModelsService.QueryModelData',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAnalyticsModelV3SemanticModelsServiceUrl({
        protoPath: '/v3/model-api/query-data',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.fields.*.numericValue' },
              { path: 'totals.fields.*.numericValue' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.fields.*.timestampValue' },
              { path: 'cacheStatus.createdAt' },
              { path: 'cacheStatus.expiresAt' },
              { path: 'totals.fields.*.timestampValue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryModelData;
}

/** Retrieves a semantic model by ID or slug. */
export function getModel(payload: object): RequestOptionsFactory<any> {
  function __getModel({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.analytics.model.v3.model',
      method: 'POST' as any,
      methodFqn: 'wix.analytics.model.v3.SemanticModelsService.GetModel',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAnalyticsModelV3SemanticModelsServiceUrl({
        protoPath: '/v3/model-api/get',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __getModel;
}

/** Retrieves a list of all available semantic models. */
export function listModels(payload: object): RequestOptionsFactory<any> {
  function __listModels({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.analytics.model.v3.model',
      method: 'POST' as any,
      methodFqn: 'wix.analytics.model.v3.SemanticModelsService.ListModels',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAnalyticsModelV3SemanticModelsServiceUrl({
        protoPath: '/v3/model-api/list',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __listModels;
}
